import {CALCULATOR_SETTINGS} from 'client/Assignments/constants';
import type {CalculatorSetting, CalculatorTier} from 'client/Assignments/constants';

import type {CalculatorType} from './CalculatorTitleBar.react';

/** Calculator tiers from least to most capable; each one allows every tier below it. */
export const TIER_ORDER: readonly CalculatorTier[] = ['none', 'basic', 'scientific', 'graphing'];

const asTier = (value: unknown): CalculatorTier => {
  const tier = typeof value === 'string' ? value.toLowerCase() : '';
  return TIER_ORDER.find((known) => known === tier) ?? 'none';
};

/**
 * The calculator a student gets on a question. A teacher's named setting holds every question to
 * that tier; following content, or having no setting at all, uses the question's own content tier.
 * Anything unrecognised resolves to `none`.
 */
export function resolveCalculatorTier(
  setting: CalculatorSetting | undefined,
  contentTier: unknown
): CalculatorTier {
  if (setting && setting !== CALCULATOR_SETTINGS.FOLLOW_CONTENT) {
    return asTier(setting);
  }
  return asTier(contentTier);
}

const MATH_CURRICULUM_AREA = 'Math';

/**
 * The tier for practice with no teacher setting: the question's own designation, else its content.
 * Temporary: undesignated math content offers every calculator until math carries designations.
 */
export function resolveContentCalculatorTier({
  questionTier,
  contentTier,
  curriculumAreaName
}: {
  questionTier: string | null | undefined;
  contentTier: unknown;
  curriculumAreaName: string | null | undefined;
}): CalculatorTier {
  if (questionTier) {
    return asTier(questionTier);
  }
  const tier = asTier(contentTier);
  return tier === 'none' && curriculumAreaName === MATH_CURRICULUM_AREA ? 'graphing' : tier;
}

/** The calculators a tier offers: the tier itself and every simpler one. */
export function calculatorTypesFor(tier: CalculatorTier): CalculatorType[] {
  return TIER_ORDER.slice(1, TIER_ORDER.indexOf(tier) + 1) as CalculatorType[];
}
